import { isRetryable, type AccountClient } from './client';
import {
  DEVICE_FIELDS,
  DEVICE_KEYS,
  IMPORT_RECORD_KEY,
  LEGACY_UNMAPPED_KEY,
  type DeviceField,
  type DocKey,
  type ListField,
} from './keys';
import * as sanitize from './sanitize';
import { isPlainObject, parseJson, safeGet, safeSet, type KeyValueStorage } from './storage';
import type { AccountState, ImportPayload, ImportReceipt } from './types';

/**
 * The move from the device to the account (CONTRACT §6).
 *
 * The rule it keeps: every saved item is at all times in at least one of three
 * places — the device's keys, the pending import, or the account. Nothing
 * leaves the device until the account has been read back and shown to hold
 * it, or the server has recorded it as unresolved inside the stored import.
 *
 *   1. Snapshot the eight keys (raw), plus mcw.legacyUnmapped.
 *   2. Write mcw.accountImport before sending.
 *   3. POST /me/import.
 *   4. GET /me/state and check every item.
 *   5. Re-read the keys; if anything changed, start again.
 *   6. Clear exactly the eight keys, then mcw.accountImport.
 *
 * Everything but `runImport` is pure.
 */

/** The eight keys exactly as stored, plus mcw.legacyUnmapped. */
export type Snapshot = Record<DeviceField | 'legacyUnmapped', string | null>;

export function readSnapshot(storage: KeyValueStorage): Snapshot {
  const out = {} as Snapshot;
  for (const field of DEVICE_FIELDS) out[field] = safeGet(storage, DEVICE_KEYS[field]);
  out.legacyUnmapped = safeGet(storage, LEGACY_UNMAPPED_KEY);
  return out;
}

export function sameSnapshot(a: Snapshot, b: Snapshot): boolean {
  return DEVICE_FIELDS.every((field) => a[field] === b[field]) && a.legacyUnmapped === b.legacyUnmapped;
}

/**
 * The payload the device's keys make: what each hook's own `read()` would show.
 *
 * Lists lose duplicates; a personal event appears once per id (the one the app
 * finds first); anything a hook would ignore is left out, since the backend
 * accepts exactly what the hooks accept.
 */
export function buildPayload(snapshot: Snapshot): ImportPayload {
  const list = (field: ListField) => [...new Set(sanitize.stringList(parseJson(snapshot[field])))];

  const seen = new Set<string>();
  const personalEvents = sanitize.personalDrafts(parseJson(snapshot.personalEvents)).filter((draft) => {
    if (seen.has(draft.id)) return false;
    seen.add(draft.id);
    return true;
  });

  const legacy = parseJson(snapshot.legacyUnmapped);
  return {
    schedule: list('schedule'),
    saved: list('saved'),
    hidden: list('hidden'),
    personalEvents,
    attendance: sanitize.attendance(parseJson(snapshot.attendance)),
    travelLegs: sanitize.travelLegs(parseJson(snapshot.travelLegs)),
    home: sanitize.home(parseJson(snapshot.home)),
    eventEdits: sanitize.eventEdits(parseJson(snapshot.eventEdits)),
    // Unparseable still rides along, as the text it is.
    legacyUnmapped: legacy === undefined ? snapshot.legacyUnmapped : legacy,
  };
}

export type Counts = Record<ListField | DocKey, number>;

const zeroCounts = (): Counts => ({
  schedule: 0,
  saved: 0,
  hidden: 0,
  personalEvents: 0,
  attendance: 0,
  travelLegs: 0,
  home: 0,
  eventEdits: 0,
});

export function payloadCounts(payload: ImportPayload): Counts {
  return {
    schedule: payload.schedule.length,
    saved: payload.saved.length,
    hidden: payload.hidden.length,
    personalEvents: payload.personalEvents.length,
    attendance: Object.keys(payload.attendance).length,
    travelLegs: Object.keys(payload.travelLegs).length,
    home: payload.home ? 1 : 0,
    eventEdits: Object.keys(payload.eventEdits).length,
  };
}

/** Anything the app would show. `[]`, `{}` and unreadable values hold nothing. */
export function payloadHasData(payload: ImportPayload): boolean {
  return Object.values(payloadCounts(payload)).some((n) => n > 0);
}

export const snapshotHasData = (snapshot: Snapshot): boolean => payloadHasData(buildPayload(snapshot));

export interface Verification {
  ok: boolean;
  /** Items the account doesn't hold, by kind. Counts only: they are reported. */
  missing: Counts;
}

/**
 * Step 4: is every item in the payload in the account?
 *
 *   schedule   in going
 *   saved      in saved ∪ going
 *   hidden     in dismissed ∪ saved ∪ going
 *   personal events, attendance, travel legs, edits   by id or key
 *   home       the account has one
 *
 * An id the receipt lists as unresolved (a list's, or a personal event's)
 * counts as held: the server keeps it in the stored import.
 */
export function verify(payload: ImportPayload, state: AccountState, receipt: ImportReceipt): Verification {
  const missing = zeroCounts();
  const going = new Set(state.intents.going);
  const saved = new Set(state.intents.saved);
  const dismissed = new Set(state.intents.dismissed);
  const unresolved = new Set(receipt.unresolved.map((u) => `${u.list}:${u.id}`));
  const held = (list: ListField, id: string, ...sets: Set<string>[]) =>
    unresolved.has(`${list}:${id}`) || sets.some((s) => s.has(id));

  for (const id of payload.schedule) if (!held('schedule', id, going)) missing.schedule += 1;
  for (const id of payload.saved) if (!held('saved', id, saved, going)) missing.saved += 1;
  for (const id of payload.hidden) if (!held('hidden', id, dismissed, saved, going)) missing.hidden += 1;

  const body = (key: DocKey) => state.documents[key]?.body;
  const personalIds = new Set(
    (Array.isArray(body('personalEvents')) ? (body('personalEvents') as unknown[]) : [])
      .map((item) => (isPlainObject(item) && typeof item.id === 'string' ? item.id : null))
      .filter((id): id is string => id !== null),
  );
  for (const draft of payload.personalEvents) {
    if (!personalIds.has(draft.id) && !unresolved.has(`personalEvents:${draft.id}`)) missing.personalEvents += 1;
  }

  for (const key of ['attendance', 'travelLegs', 'eventEdits'] as const) {
    const account = isPlainObject(body(key)) ? (body(key) as Record<string, unknown>) : {};
    for (const k of Object.keys(payload[key])) if (!(k in account)) missing[key] += 1;
  }

  if (payload.home && !body('home')) missing.home = 1;

  return { ok: Object.values(missing).every((n) => n === 0), missing };
}

// --- the pending import --------------------------------------------------------

export interface ImportRecord {
  workosUserId: string;
  importId: string;
  snapshot: Snapshot;
  createdAt: string;
  /**
   * The server answered with this id's stored receipt and the account still
   * failed the check: the account has moved on since (say, an event removed on
   * another device), or the server holds the id for another account. The
   * next attempt merges again under a new id.
   */
  stale?: boolean;
}

export function readRecord(storage: KeyValueStorage): ImportRecord | null {
  const value = parseJson(safeGet(storage, IMPORT_RECORD_KEY));
  if (!isPlainObject(value) || !isPlainObject(value.snapshot)) return null;
  if (typeof value.workosUserId !== 'string' || typeof value.importId !== 'string') return null;
  return value as unknown as ImportRecord;
}

/** Step 2: the stored id, only when the user and the snapshot both match. */
export function recordFor(
  existing: ImportRecord | null,
  workosUserId: string,
  snapshot: Snapshot,
  newId: () => string,
  now: Date,
): ImportRecord {
  if (
    existing &&
    !existing.stale &&
    existing.workosUserId === workosUserId &&
    sameSnapshot(existing.snapshot, snapshot)
  ) {
    return existing;
  }
  return { workosUserId, importId: newId(), snapshot, createdAt: now.toISOString() };
}

// --- the runner ----------------------------------------------------------------

export type ImportOutcome =
  /** Nothing on the device to move. */
  | { kind: 'empty' }
  | { kind: 'done'; state: AccountState; receipt: ImportReceipt; counts: Counts }
  /** Everything kept; worth trying again soon. */
  | { kind: 'retry'; reason: 'network' | 'unverified' | 'changed'; status?: number; missing?: Counts }
  /** Everything kept; the server refused it, so only a later load tries again. */
  | { kind: 'rejected'; status: number; code: string | null };

export interface ImportDeps {
  storage: KeyValueStorage;
  client: Pick<AccountClient, 'postImport' | 'getState'>;
  workosUserId: string;
  newId: () => string;
  now?: () => Date;
}

/** Changes during the import send it again, a few times, before waiting for a later try. */
const MAX_ROUNDS = 3;

function failure(err: unknown): ImportOutcome {
  const status = typeof err === 'object' && err && 'status' in err ? Number(err.status) : 0;
  if (isRetryable(err)) return { kind: 'retry', reason: 'network', status };
  const code = typeof err === 'object' && err && 'code' in err && typeof err.code === 'string' ? err.code : null;
  return { kind: 'rejected', status, code };
}

export async function runImport(deps: ImportDeps): Promise<ImportOutcome> {
  const { storage, client, workosUserId, newId } = deps;
  const now = deps.now ?? (() => new Date());

  for (let round = 0; round < MAX_ROUNDS; round += 1) {
    // 1.
    const snapshot = readSnapshot(storage);
    const payload = buildPayload(snapshot);
    if (!payloadHasData(payload)) return { kind: 'empty' };

    // 2. If the record can't be written (site data full), the keys still hold
    // everything; only the id's reuse is lost, and the server's merge is a union.
    const record = recordFor(readRecord(storage), workosUserId, snapshot, newId, now());
    safeSet(storage, IMPORT_RECORD_KEY, JSON.stringify(record));

    // 3.
    let receipt: ImportReceipt;
    try {
      receipt = await client.postImport(record.importId, payload);
    } catch (err) {
      // The server holds this id for another account: never send it again.
      if ((err as { code?: unknown } | null)?.code === 'IMPORT_ID_CONFLICT') {
        safeSet(storage, IMPORT_RECORD_KEY, JSON.stringify({ ...record, stale: true }));
      }
      return failure(err);
    }

    // 4.
    let state: AccountState;
    try {
      state = await client.getState();
    } catch (err) {
      return failure(err);
    }
    const check = verify(payload, state, receipt);
    if (!check.ok) {
      if (receipt.replayed) safeSet(storage, IMPORT_RECORD_KEY, JSON.stringify({ ...record, stale: true }));
      return { kind: 'retry', reason: 'unverified', missing: check.missing };
    }

    // 5. Synchronous from here to the end: nothing else in this tab can write
    // between the check and the clear.
    if (!sameSnapshot(readSnapshot(storage), snapshot)) continue;

    // 6.
    for (const field of DEVICE_FIELDS) safeSet(storage, DEVICE_KEYS[field], null);
    safeSet(storage, IMPORT_RECORD_KEY, null);
    return { kind: 'done', state, receipt, counts: payloadCounts(payload) };
  }

  return { kind: 'retry', reason: 'changed' };
}
